// @spec SHL-STR-047
'use client';

import * as React from 'react';

interface AnnouncerContextValue {
  announce: (message: string, options?: { assertive?: boolean }) => void;
}

const AnnouncerContext = React.createContext<AnnouncerContextValue | null>(null);

interface AnnouncerProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for the screen reader announcer.
 *
 * Manages a global live region for announcing dynamic content changes to screen readers.
 * Provides context for programmatic announcements throughout the app.
 *
 * @example
 * <AnnouncerProvider>
 *   <App />
 * </AnnouncerProvider>
 */
function AnnouncerProvider({ children }: AnnouncerProviderProps) {
  const [politeMessage, setPoliteMessage] = React.useState('');
  const [assertiveMessage, setAssertiveMessage] = React.useState('');

  const announce = React.useCallback((message: string, options?: { assertive?: boolean }) => {
    if (options?.assertive) {
      // Clear then set to ensure repeat announcements work
      setAssertiveMessage('');
      // Use setTimeout to ensure the DOM updates between clear and set
      setTimeout(() => {
        setAssertiveMessage(message);
      }, 50);
      // Clear after announcement to allow repeat
      setTimeout(() => {
        setAssertiveMessage('');
      }, 1000);
    } else {
      setPoliteMessage('');
      setTimeout(() => {
        setPoliteMessage(message);
      }, 50);
      setTimeout(() => {
        setPoliteMessage('');
      }, 1000);
    }
  }, []);

  const contextValue = React.useMemo(() => ({ announce }), [announce]);

  return (
    <AnnouncerContext.Provider value={contextValue}>
      {children}
      {/* Polite live region - waits for user to finish current task */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {politeMessage}
      </div>
      {/* Assertive live region - interrupts current announcements */}
      <div role="alert" aria-live="assertive" aria-atomic="true" className="sr-only">
        {assertiveMessage}
      </div>
    </AnnouncerContext.Provider>
  );
}

function useAnnouncerContext(): AnnouncerContextValue {
  const context = React.useContext(AnnouncerContext);
  if (!context) {
    throw new Error('useAnnouncerContext must be used within an AnnouncerProvider');
  }
  return context;
}

export { AnnouncerProvider, AnnouncerContext, useAnnouncerContext };
export type { AnnouncerContextValue, AnnouncerProviderProps };
